import { Maybe } from '@lib/graphql-types';
import { Component, Host, Prop, h, Element } from '@stencil/core';
import { formatOdds } from '@utils/bettingUtils';
import { getBookAssetsById } from '@utils/books';
import { toHexColor } from '@utils/colorUtils';
import { capitalizeFirstLetter } from '@utils/stringUtils';

// https://www.figma.com/design/TLXf8t3qC5vIMbEC9T3ODH/Bettable-Odds?node-id=2627-31369&p=f&m=dev
export type SportslinePickSnippetCardProps = {
  activeColor?: Maybe<string>;
  label?: Maybe<string>;
  labelLong?: Maybe<string>;
  odds?: Maybe<string | number>;
  gameInfoLabel?: Maybe<string>;
  type?: Maybe<'standard' | 'player' | 'team' | 'game'>;
  marketLabel?: Maybe<string>;
  playerName?: Maybe<string>;
  playerId?: Maybe<string | number>;
  teamName?: Maybe<string>;
  teamId?: Maybe<string | number>;
  side?: Maybe<'over' | 'under' | 'home' | 'away' | 'yes' | 'no' | null>;
  assetSize?: Maybe<'small' | 'medium' | 'large'>;
};

@Component({
  tag: 'sportsline-pick-snippet-card',
  styleUrl: 'sportsline-pick-snippet-card.css',
  shadow: true,
})
export class SportslinePickSnippetCard {
  /**
   * isBranded
   */
  @Prop() isBranded: boolean = false;

  /**
   * Book ID, e.g., "35"
   */
  @Prop() bookId?: string | number | null;

  /**
   * Book Name, e.g., "FanDuel"
   */
  @Prop() bookName?: string | null;

  /**
   * Slogan for the book.
   */
  @Prop() bookSlogan?: string | null;
  /**
   * Default color for the card.
   */
  @Prop() activeColor: SportslinePickSnippetCardProps['activeColor'] = '#ce1141';

  /**
   * label for the card.
   */
  @Prop() label: SportslinePickSnippetCardProps['label'];

  /**
   * Odds label for the card.
   */
  @Prop() odds: SportslinePickSnippetCardProps['odds'];

  /**
   * game info label for the card.
   * This will be displayed in the card.
   */
  @Prop() gameInfoLabel: SportslinePickSnippetCardProps['gameInfoLabel'];

  /**
   * Type of the card.
   * This will determine the layout and content of the card.
   * Possible values are 'standard', 'player', 'team', and 'game'.
   */
  @Prop() type: SportslinePickSnippetCardProps['type'] = 'standard';

  /**
   * Market Label for the card.
   */
  @Prop() marketLabel: SportslinePickSnippetCardProps['marketLabel'];

  /**
   * Player Name for the card.
   */
  @Prop() playerName: SportslinePickSnippetCardProps['playerName'];

  /**
   * player id
   */
  @Prop() playerId: SportslinePickSnippetCardProps['playerId'];

  /**
   * team name
   */
  @Prop() teamName: SportslinePickSnippetCardProps['teamName'];

  /**
   * player id
   */
  @Prop() teamId: SportslinePickSnippetCardProps['teamId'];

  /**
   * side
   */
  @Prop() side: SportslinePickSnippetCardProps['side'];

  /**
   * Asset size for the card.
   * This will be used to style the card.
   */
  @Prop() assetSize: 'small' | 'medium' | 'large' = 'medium';

  @Element() el: HTMLSportslinePickSnippetCardElement;

  private isYesNoMarket() {
    return ['yes', 'no'].includes(this.side?.toLowerCase());
  }

  private isPlayerProp() {
    return this.type === 'player';
  }

  private isTeamProp() {
    return this.type === 'team';
  }

  private get getActiveColor() {
    return toHexColor({ color: this.activeColor, fallback: '#ce1141' });
  }

  private get getSelectionAsset() {
    const side = this.side?.toUpperCase() || null;
    const type = this.type?.toLowerCase() || 'standard';

    switch (type) {
      case 'player':
        return (
          <sportsline-player-avatar
            style={{
              '--sportsline-player-avatar-background-color': this.getActiveColor,
            }}
            playerId={(this.playerId ?? -1).toString()}
            size={this.assetSize}
          />
        );
      case 'team':
        return <sportsline-team-logo variant="dark" teamId={this.teamId.toString()} size={this.assetSize} />;

      default: {
        if (['OVER', 'UNDER'].includes(side)) {
          return <sportsline-over-under-image size={this.assetSize} selection={this.side} />;
        }

        if (Boolean(this.teamId)) {
          return <sportsline-team-logo variant="dark" teamId={this.teamId.toString()} size={this.assetSize} />;
        }

        return null;
      }
    }
  }

  private get getLabel() {
    if (this.isPlayerProp()) {
      if (this.isYesNoMarket()) {
        return this.playerName;
      }
    }
    return this.label;
  }

  private get getSubLabel() {
    const marketName = capitalizeFirstLetter(this.marketLabel?.toLowerCase() || '');
    if (this.isPlayerProp()) {
      if (this.isYesNoMarket()) {
        return [this.marketLabel];
      }
      return [this.playerName, marketName];
    }
    //
    if (this.isTeamProp()) {
      return [this.teamName, marketName];
    }
    //
    return [marketName];
  }

  render() {
    const asset = this.getSelectionAsset;
    const isOverUnderIcon = asset?.$tag$ === 'sportsline-over-under-image';
    const isBranded = Boolean(this.isBranded) && Boolean(this.bookId);

    return (
      <Host
        style={{
          '--sportsline-pick-snippet-card-team-color': this.getActiveColor,
          '--padding-top': Boolean(isBranded) ? '2.625rem' : '0.75rem',
          'display': 'block',
        }}
      >
        {isBranded && (
          <div
            class={{
              'sportsline-pick-snippet-card__header-branded': true,
              [`sportsline-pick-snippet-card__header-branded--${this.bookId}`]: Boolean(this.bookId),
            }}
          >
            <div
              style={{
                '--image-width': '14px',
                '--image-height': '14px',
              }}
              class={{
                'sportsline-pick-snippet-card__header-branded__icon': true,
              }}
            >
              <sportsline-image props={{ src: getBookAssetsById(`${this.bookId}`).darkIcon, alt: this.bookName }} />
            </div>
            <div
              class={{
                'sportsline-pick-snippet-card__header-branded__name': true,
              }}
            >
              {`${this.bookName} Sportsbook`}
            </div>
          </div>
        )}
        <div
          class={{
            'sportsline-pick-snippet-card': true,
          }}
        >
          <div
            class={{
              'sportsline-pick-snippet-card__content-wrapper': true,
            }}
          >
            <div
              class={{
                'sportsline-pick-snippet-card__team-color--over-under': isOverUnderIcon,
                'sportsline-pick-snippet-card__team-color': !isOverUnderIcon,
              }}
            />
            <div
              class={{
                'sportsline-pick-snippet-card__content': true,
              }}
            >
              {!isBranded && asset}
              <div
                class={{
                  'sportsline-pick-snippet-card__pick-details': true,
                }}
              >
                <div
                  class={{
                    'sportsline-pick-snippet-card__pick-label': true,
                  }}
                >
                  {this.getLabel}
                  <span
                    class={{
                      'sportsline-pick-snippet-card__pick-label--odds': true,
                    }}
                  >
                    {!(this.odds === null || this.odds === undefined) ? formatOdds(this.odds) : null}
                  </span>
                </div>
                <div
                  class={{
                    'sportsline-pick-snippet-card__market': true,
                  }}
                >
                  {this.getSubLabel.filter(Boolean).map(v => (
                    <span key={v}>{v}</span>
                  ))}
                </div>
                <div
                  class={{
                    'sportsline-pick-snippet-card__metadata': true,
                  }}
                >
                  {this.gameInfoLabel}
                </div>
              </div>
            </div>
          </div>
          <div
            class={{
              'sportsline-pick-snippet-card__actions': true,
            }}
          >
            <slot></slot>
          </div>
        </div>
        <div
          class={{
            'sportsline-pick-snippet-parlay-card__disclaimer': true,
          }}
          style={{
            ...(isBranded && {
              '--padding-inline': '1rem',
              '--padding-block': '0',
              '--padding-block-top': '0.75rem',
            }),
          }}
        >
          <slot name="disclaimer"></slot>
        </div>
      </Host>
    );
  }
}
